import React, {Fragment, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Nav, NavDropdown} from 'react-bootstrap';
import {LinkContainer} from 'react-router-bootstrap';
import {useLocation} from 'react-router-dom';
import {useDebouncedCallback} from 'use-debounce';
import {AuthContext} from '../../context/auth-context';
import useBootstrapBreakpoint from '../../hooks/useBootstrapBreakpoint';
import useHover from '../../hooks/useHover';
import Link from '../../components/analytics/Link';
import {legacyRedirectURL} from '../../util/dashboards';
import headerDropdownStyles from './css/HeaderDropdown.module.scss';
import styles from './css/HealthcareDropdown.module.scss';

// A navbar dropdown over a static list of items from util/dashboards.js. In-app `path`s render as
// router links, legacy `href`s go through the session hand-off. Which entries a user sees is decided
// per item by its isEntitled predicate. Used for the DATA & DASHBOARDS tab.
export default function LinksDropdown(props) {
	const {title, items: allItems} = props;
	const [showDropdown, setShowDropdown] = useState(false);
	const [containerIsHovered, setContainerIsHovered] = useState(false);
	const [panelLeft, setPanelLeft] = useState(0);
	const [adjustedLeft, setAdjustedLeft] = useState(null);
	const {isAuthenticated, userData} = useContext(AuthContext);
	const location = useLocation();
	const [hoverRef, isHovered] = useHover();
	const buttonRef = useRef(null);
	const panelRef = useRef(null);

	const {callback: debouncedSetShowDropdown} = useDebouncedCallback((show) => setShowDropdown(show), 25);

	const bootstrapBreakpoint = useBootstrapBreakpoint();

	useEffect(() => {
		if (containerIsHovered || isHovered) {
			if (buttonRef.current) {
				const rect = buttonRef.current.getBoundingClientRect();
				setPanelLeft(rect.left + rect.width / 2);
			}
			debouncedSetShowDropdown(true);
		} else {
			debouncedSetShowDropdown(false);
		}
		// eslint-disable-next-line
	}, [containerIsHovered, isHovered]);

	useEffect(() => {
		setShowDropdown(false);
	}, [location]);

	// The panel is centred under the button; for the last tabs that can run off the viewport edge.
	useLayoutEffect(() => {
		if (showDropdown && panelRef.current) {
			const rect = panelRef.current.getBoundingClientRect();
			const vw = window.innerWidth;
			if (rect.right > vw - 10) {
				setAdjustedLeft(panelLeft - (rect.right - (vw - 10)));
			} else if (rect.left < 10) {
				setAdjustedLeft(panelLeft + (10 - rect.left));
			} else {
				setAdjustedLeft(panelLeft);
			}
		} else {
			setAdjustedLeft(null);
		}
	}, [showDropdown, panelLeft]);

	if (!isAuthenticated) return null;

	const items = allItems.filter((item) => item.isEntitled(userData));

	if (items.length === 0) return null;

	if (bootstrapBreakpoint === 'sm' || bootstrapBreakpoint === 'xs') {
		return (
			<NavDropdown className="xsMarketDropdownContainer" title={title}>
				{items.map((item) =>
					item.href ? (
						<NavDropdown.Item key={item.key} href={legacyRedirectURL(item.href)} active={false}>
							{item.label}
						</NavDropdown.Item>
					) : (
						<LinkContainer key={item.key} to={item.path}>
							<NavDropdown.Item active={false}>{item.label}</NavDropdown.Item>
						</LinkContainer>
					)
				)}
			</NavDropdown>
		);
	}

	return (
		<Fragment>
			<Nav.Link
				className={`${styles.healthcareButton} ${headerDropdownStyles.dropdownButton} ${showDropdown ? 'active' : ''}`}
				onClick={() => setShowDropdown(!showDropdown)}
				ref={(el) => {
					hoverRef(el);
					buttonRef.current = el;
				}}
			>
				{title}
			</Nav.Link>
			{showDropdown && (
				<div
					ref={panelRef}
					className={styles.dropdownPanel}
					style={{left: adjustedLeft ?? panelLeft, transform: 'translateX(-50%)'}}
					onMouseEnter={() => setContainerIsHovered(true)}
					onMouseLeave={() => setContainerIsHovered(false)}
				>
					<div className={styles.columnsGrid}>
						<div className={`${styles.column} ${styles.lastColumn}`}>
							{items.map((item) => (
								<div key={item.key}>
									{item.href ? (
										<a
											className={styles.sectionTitle}
											href={legacyRedirectURL(item.href)}
											onClick={() => setShowDropdown(false)}
										>
											{item.label}
										</a>
									) : (
										<Link
											onClick={() => setShowDropdown(false)}
											className={styles.sectionTitle}
											to={item.path}
											description="navbar"
										>
											{item.label}
										</Link>
									)}
								</div>
							))}
						</div>
					</div>
				</div>
			)}
		</Fragment>
	);
}
